import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * One owner for "submit this batch for evaluation".
 *
 * Two screens need this action — the submissions table and a single submission's detail page — and
 * a run is billed, so the two must not drift into two slightly different requests with two
 * different ideas of what to invalidate afterwards. Following `vendor-profile.ts`: the endpoint,
 * the cache keys it invalidates, and the error handling live together.
 *
 * The refusal text is passed through verbatim. The admission gates answer in words a vendor can act
 * on — `checks_in_progress`, `no_mcap_files`, "A file did not pass its checks (…)" — and a generic
 * message here would throw away the only part that says what to do next.
 */
export function useSubmitForEvaluation(options: { onError?: (message: string) => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (datasetId: string) => api(`/datasets/${datasetId}/submit`, { method: 'POST' }),
    onSuccess: (_data, datasetId) => {
      options.onError?.('');
      // The list and the one batch: submitting changes the stage on both, and the detail screen is
      // where a vendor most often does it.
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'datasets'] });
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'dataset', datasetId] });
    },
    onError: cause => options.onError?.(cause instanceof ApiError ? cause.message : 'Could not submit for evaluation.')
  });
}

/**
 * Whether the server would currently accept a submission.
 *
 * `ready_for_evaluation` is the only stage the admission gates admit, and it is derived server-side
 * from the current file rows — so this is a reading of the server's answer, not a second opinion
 * about it (frontend.md section 3).
 */
export const canSubmit = (lifecycleStatus: string) => lifecycleStatus === 'ready_for_evaluation';
